import React from 'react'
import { BLOCKS } from '@contentful/rich-text-types'
import classNames from 'classnames'
import { ISocialShareBlockFields } from 'src/types/generated/contentful'
import TextBlock from '@/components/common/TextBlock'
import Image from '@/components/contentful/Image'
import useCampaignAssets from '@/services/hooks/useCampaignAssets'
import { contentUtil } from '@/utils'
import SocialLink from '../SocialLink'
import Type from '../Type'

/* eslint-disable @typescript-eslint/no-explicit-any */
const textOverrides = {
  [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
    return (
      <Type as="p" variant="caption-lg" className="text-core-gray-700">
        {children}
      </Type>
    )
  },
  [BLOCKS.HEADING_4]: (node: any, children: any) => {
    return (
      <Type as="h4" variant="title-lg" className="mb-2 font-semibold">
        {children}
      </Type>
    )
  },
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface Props {
  campaignName: string
  campaignSlug: string
  className?: string
  content: ISocialShareBlockFields
}

const CampaignShareBox: React.FC<Props> = ({
  campaignName,
  campaignSlug,
  content,
  className,
}) => {
  const { links, text } = content
  const { campaignAssets } = useCampaignAssets(campaignSlug)
  const { image, slug = '' } = campaignAssets || {}

  return (
    <div
      className={classNames(
        'flex flex-col gap-[18px] w-full rounded-2xl border border-core-gray-300 bg-white px-4 py-6',
        className
      )}
    >
      {text && (
        <TextBlock
          document={text}
          optionOverrides={textOverrides}
          className="!mb-0"
        />
      )}
      {image && (
        <Image image={image} className="w-full rounded-lg" width={534} />
      )}
      <div
        className={classNames('flex flex-wrap gap-2 w-full', {
          'justify-between': links.length > 2,
          'justify-around': links.length <= 2,
        })}
      >
        {links.map((rawLink) => {
          const link = contentUtil.mapContent(rawLink)
          if (!link) return null

          let emailSubject
          if (link.emailSubject)
            emailSubject = link.emailSubject.replace(
              /{{campaign.name}}/g,
              campaignName
            )

          return (
            <SocialLink
              key={link.id}
              className="flex-grow"
              emailSubject={emailSubject}
              name={link.name}
              icon={link.icon}
              preconfigured={link.preconfigured}
              url={link.url}
              path={slug}
            />
          )
        })}
      </div>
    </div>
  )
}

export default CampaignShareBox
